import { useState } from 'react'
import { StoredProfile, saveProfile } from '@/utils/storage'
import {
  calculateLifePathNumber,
  calculateDestinyNumber,
  calculateSoulUrgeNumber,
  calculateExpressionNumber,
  calculateBirthdayNumber,
} from '@/utils/numerology'
import { texts } from '@/texts'

interface ProfileEditFormProps {
  profile: StoredProfile
  onSave: (updatedProfile: StoredProfile) => void
  onCancel: () => void
}

export function ProfileEditForm({
  profile,
  onSave,
  onCancel,
}: ProfileEditFormProps) {
  const [name, setName] = useState(profile.name)
  const [dateOfBirth, setDateOfBirth] = useState(profile.dateOfBirth)
  const [isSaving, setIsSaving] = useState(false)

  const hasChanges = name !== profile.name || dateOfBirth !== profile.dateOfBirth
  const isValid = name.trim() && dateOfBirth && !isSaving

  const handleSave = async () => {
    if (!isValid) return

    setIsSaving(true)

    // Recalculate all numbers
    const lifePathNumber = calculateLifePathNumber(dateOfBirth)
    const destinyNumber = calculateDestinyNumber(name)
    const soulUrgeNumber = calculateSoulUrgeNumber(name)
    const expressionNumber = calculateExpressionNumber(name)
    const birthdayNumber = calculateBirthdayNumber(dateOfBirth)

    // Save updated profile
    const updatedProfile = saveProfile(name, dateOfBirth, {
      lifePathNumber,
      destinyNumber,
      soulUrgeNumber,
      expressionNumber,
      birthdayNumber,
    })

    // Small delay for better UX
    setTimeout(() => {
      setIsSaving(false)
      onSave(updatedProfile)
    }, 300)
  }

  return (
    <div style={{
      minHeight: '100vh',
      padding: 'var(--spacing-4)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-start',
      paddingTop: 'var(--spacing-8)',
    }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-8)' }}>
        <h2 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '1.75rem',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--mg-gold-soft)',
          margin: '0 0 var(--spacing-2) 0',
          textShadow: '0 0 14px rgba(232, 200, 122, 0.35)',
        }}>
          {texts.profileEditForm.title}
        </h2>
        <p style={{ fontSize: '0.95rem', color: 'var(--mg-text-muted)', margin: 0 }}>
          {texts.profileEditForm.subtitle}
        </p>
      </div>

      {/* Form Container */}
      <div style={{ maxWidth: '400px', margin: '0 auto', width: '100%' }}>
        <div className="mg-card" style={{ padding: 'var(--spacing-6)' }}>
          {/* Name Field */}
          <div style={{ marginBottom: 'var(--spacing-5)' }}>
            <label style={{
              display: 'block',
              marginBottom: 'var(--spacing-2)',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: '0.95rem',
              color: 'var(--mg-text)',
            }}>
              {texts.profileEditForm.nameLabel}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isSaving}
              style={{ width: '100%', opacity: isSaving ? 0.6 : 1 }}
            />
          </div>

          {/* Date Field */}
          <div style={{ marginBottom: 'var(--spacing-6)' }}>
            <label style={{
              display: 'block',
              marginBottom: 'var(--spacing-2)',
              fontWeight: 'var(--font-weight-semibold)',
              fontSize: '0.95rem',
              color: 'var(--mg-text)',
            }}>
              {texts.profileEditForm.dateLabel}
            </label>
            <input
              type="date"
              value={dateOfBirth}
              onChange={(e) => setDateOfBirth(e.target.value)}
              disabled={isSaving}
              style={{ width: '100%', opacity: isSaving ? 0.6 : 1, cursor: isSaving ? 'not-allowed' : 'pointer' }}
            />
          </div>

          {/* Button Group */}
          <div style={{ display: 'flex', gap: 'var(--spacing-3)', width: '100%' }}>
            <button
              className="mg-btn-ghost"
              onClick={onCancel}
              disabled={isSaving}
              style={{ flex: 1, opacity: isSaving ? 0.5 : 1 }}
            >
              {texts.profileEditForm.cancelButton}
            </button>
            <button
              className="mg-btn-primary"
              onClick={handleSave}
              disabled={!isValid || !hasChanges}
              style={{ flex: 1 }}
            >
              {isSaving ? texts.profileEditForm.savingButton : texts.profileEditForm.saveButton}
            </button>
          </div>

          {/* Helper Text */}
          <p style={{
            fontSize: '0.75rem',
            color: 'var(--mg-text-dim)',
            textAlign: 'center',
            margin: 'var(--spacing-4) 0 0 0',
          }}>
            {texts.profileEditForm.helperText}
          </p>
        </div>
      </div>
    </div>
  )
}
